import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUI, useTransactions, useAuth } from '../hooks';
import ReleaseFunds from '../components/ReleaseFunds/ReleaseFunds';
import { TrackingStatusBanner } from '../components/TrackingStatusBanner';
import { TransactionProgressIndicator, shouldShowProgressIndicator } from '../components/TransactionProgressIndicator';
import { Transaction } from '../types';

/**
 * ReleaseFundsScreen component
 *
 * This screen displays the Release Funds page for a specific transaction.
 * It extracts the transaction ID from URL parameters and passes it to the ReleaseFunds component.
 */
const ReleaseFundsScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useUI();
  const { getTransaction } = useTransactions();
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  
  // Get transaction data from location state if available
  const transactionFromState = location.state?.transactionData as Transaction | undefined;
  
  console.log(`[DEBUG] ReleaseFundsScreen: Rendering with id = ${id}, isLoading = ${isLoading}`);
  console.log('ReleaseFundsScreen: location state =', location.state);
  
  // Memoize the prefetch function to prevent it from changing on every render
  const prefetchTransaction = useCallback(async (transactionId: string) => {
    console.log(`[DEBUG] ReleaseFundsScreen: Prefetching transaction ${transactionId}`);
    setIsLoading(true);
    
    try {
      const transactionData = await getTransaction(transactionId);
      console.log('[DEBUG] ReleaseFundsScreen: Transaction prefetched:', transactionData);
      
      // Validate transaction data
      if (!transactionData || typeof transactionData !== 'object' || !transactionData.id) {
        throw new Error('Invalid transaction data received');
      }
      
      // Set the transaction state
      setTransaction(transactionData);
      setIsLoading(false);
    } catch (error) {
      console.error('[DEBUG] ReleaseFundsScreen: Error prefetching transaction:', error);
      setIsLoading(false);
      showToast('error', 'Failed to load transaction details');
    }
  }, [getTransaction, showToast]);

  // Redirect to home if no ID is provided and handle transaction data
  useEffect(() => {
    console.log(`[DEBUG] ReleaseFundsScreen: Effect running with id = ${id}, isLoading = ${isLoading}`);
    
    if (!id) {
      showToast('error', 'No transaction ID provided');
      navigate('/home');
      return;
    }
    
    // If we have transaction data from location state, use it
    if (transactionFromState) {
      console.log('[DEBUG] ReleaseFundsScreen: Using transaction data from location state');
      setTransaction(transactionFromState);
    } else if (!transaction && !isLoading) {
      // If we don't have transaction data, prefetch it
      console.log('[DEBUG] ReleaseFundsScreen: No transaction data, calling prefetch');
      prefetchTransaction(id);
    }
  }, [id, navigate, showToast, transactionFromState, prefetchTransaction, isLoading, transaction]);

  console.log(`[DEBUG] ReleaseFundsScreen: Before render, id = ${id}, special ID check: ${id === 'ecd4a49d-32b2-4c0e-b651-18ae99b7bff2'}`);
  
  // Determine user role based on transaction and current user
  const getUserRole = (): 'buyer' | 'seller' => {
    if (!transaction || !currentUser) return 'buyer'; // Default to buyer
    return transaction.buyerId === currentUser.id ? 'buyer' : 'seller';
  };

  return (
    <div className="container">
      {id && transaction && (
        <>
          
          {/* TrackingStatusBanner - positioned at the very top for maximum visibility */}
          <TrackingStatusBanner
            transaction={transaction}
            userRole={getUserRole()}
          /> 
          
          {/* TransactionProgressIndicator - shows progress context */}
          {shouldShowProgressIndicator(transaction) && (
            <TransactionProgressIndicator
              transaction={transaction}
              size="small"
              showLabels={true}
              showIcons={true}
              className="release-funds-progress"
            />
          )}
              
          <ReleaseFunds
            key={id}
            transactionId={id}
          />
        </>
      )}
      {id && !transaction && (
        <ReleaseFunds
          key={id}
          transactionId={id}
        />
      )}
    </div>
  );
};

export default ReleaseFundsScreen;